import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { GROUPS, imageSrc, SECTIONS, sectionOf, type PostSummary } from "@/lib/site";
import type { Season } from "@/hooks/useSeason";
import { CardImage } from "@/components/site/CardImage";

export { SeasonHero, HowItWorks } from "@/components/site/Hero";

export function SectionLabel({ slug }: { slug: string }) {
  const s = sectionOf(slug);
  return s ? <span className="pill" data-testid="section-label">{s.short}</span> : null;
}

export function GiftCard({ post, index = 0, big = false }: { post: PostSummary; index?: number; big?: boolean }) {
  return (
    <Link to={`/${post.slug}`} className={`pop-card group flex flex-col overflow-hidden fade-up ${big ? "sm:col-span-2 sm:row-span-2" : ""}`}
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }} data-testid={`gift-card-${post.slug}`}>
      <div className={`relative overflow-hidden border-b border-[#F5F5F4] bg-[#F5F5F4] ${big ? "aspect-[4/3] sm:aspect-auto sm:flex-1" : "aspect-[4/3]"}`}>
        <CardImage post={post} />
      </div>
      <div className="flex flex-col gap-2 p-5">
        <div><SectionLabel slug={post.section} /></div>
        <h3 className={`shop-h2 leading-snug ${big ? "text-xl sm:text-2xl" : "text-base"}`}>{post.title}</h3>
        <p className={`line-clamp-2 text-sm text-[#78716C] ${big ? "" : "hidden lg:block"}`}>{post.description}</p>
      </div>
    </Link>
  );
}

export function SectionHeading({ eyebrow, title, sub, to, label, testId }: { eyebrow?: string; title: string; sub?: string; to?: string; label?: string; testId?: string }) {
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div>
        {eyebrow && <p className="shop-eyebrow">{eyebrow}</p>}
        <h2 className="shop-h2 mt-2 text-3xl sm:text-4xl">{title}</h2>
        {sub && <p className="mt-2 text-base text-[#78716C]" data-testid="right-now-sub">{sub}</p>}
      </div>
      {to && <Link to={to} className="btn-line !py-2.5" data-testid={testId}>{label} <ArrowRight className="h-4 w-4" /></Link>}
    </div>
  );
}

export function RightNow({ season }: { season: Season }) {
  if (!season.featured.length) return null;
  return (
    <section id="right-now" className="mx-auto max-w-7xl scroll-mt-24 px-5 pt-20 sm:px-8" data-testid="home-right-now">
      <SectionHeading eyebrow={season.key === "everyday" ? "Fresh picks" : "This season's picks"} title="Right now"
        sub={season.key === "everyday" ? "A fresh mix of easy, thoughtful makes for anyone on your list." : season.headline} />
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:grid-rows-2">
        {season.featured.map((p, i) => <GiftCard key={p.slug} post={p} index={i} big={i === 0} />)}
      </div>
    </section>
  );
}

export function SectionTiles({ posts }: { posts: PostSummary[] | null }) {
  const photo = (slug: string) => posts?.find((p) => p.section === slug && p.image)?.image;
  const live = (slug: string) => !posts || posts.some((p) => p.section === slug);
  return (
    <section id="find-a-gift" className="mx-auto max-w-7xl scroll-mt-24 px-5 pt-24 sm:px-8" data-testid="home-sections">
      <SectionHeading eyebrow="Find a gift" title="Where do you want to start?" />
      <div className="space-y-14">
        {GROUPS.map((g) => (
          <div key={g.key} data-testid={`home-group-${g.key}`}>
            <div className="mb-5 flex flex-wrap items-baseline gap-x-3 border-b border-[#E7E5E4] pb-3">
              <h3 className="shop-h2 text-xl">{g.label}</h3>
              <p className="text-sm text-[#78716C]">{g.sub}</p>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
              {SECTIONS.filter((s) => s.group === g.key && live(s.slug)).map((s, i) => (
                <Link key={s.slug} to={`/gifts/${s.slug}`} className="group block fade-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`home-section-${s.slug}`}>
                  <div className="overflow-hidden rounded-2xl bg-[#F5F5F4]">
                    <img src={imageSrc(photo(s.slug) || s.image)} alt="" loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <p className="mt-3 flex items-center gap-1.5 font-['Plus_Jakarta_Sans'] text-sm font-bold text-[#1C1917] sm:text-base">{s.name}<ArrowRight className="h-4 w-4 shrink-0 transition-transform duration-150 group-hover:translate-x-0.5" /></p>
                  <p className="mt-1 line-clamp-2 text-xs text-[#78716C] sm:text-sm">{s.blurb}</p>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
